import React, { useEffect, useRef, useState } from "react";
import { Input, Spin, Tooltip, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import type { InputRef } from "antd";
import { useTranslation } from "react-i18next";
import { updateOrderNotesApi } from "@/api/order";
import { CheckIcon, CloseIcon, EditIcon } from "@/components/icons";

interface InlineEditableNoteProps {
  orderId: string;
  note?: string;
  onSave?: (orderId: string, note: string) => void;
  maxWidth?: number;
  variant?: "column" | "compact";
}

const InlineEditableNote: React.FC<InlineEditableNoteProps> = ({
  orderId,
  note = "",
  onSave,
  maxWidth = 200,
  variant = "column",
}) => {
  const { t } = useTranslation();
  const normalizedNote = note ?? "";
  const [displayNote, setDisplayNote] = useState(normalizedNote);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(normalizedNote);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const editStartedAtRef = useRef(0);
  const savingRef = useRef(false);
  const cancellingRef = useRef(false);

  useEffect(() => {
    // Keep the edit baseline stable so an untouched draft cannot overwrite a newer note.
    if (!editing) {
      setDisplayNote(normalizedNote);
    }
  }, [normalizedNote, editing]);

  useEffect(() => {
    if (!editing) return;
    const frameId = window.requestAnimationFrame(() => {
      inputRef.current?.focus({ cursor: "end" });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [editing]);

  const cancelEdit = () => {
    if (savingRef.current) return;
    cancellingRef.current = true;
    setDraft(displayNote);
    setEditing(false);
  };

  const saveNote = async () => {
    if (!orderId || savingRef.current || cancellingRef.current) return;

    if (draft === displayNote) {
      setEditing(false);
      return;
    }

    try {
      savingRef.current = true;
      setSaving(true);
      await updateOrderNotesApi(orderId, { note: draft });
      setDisplayNote(draft);
      onSave?.(orderId, draft);
      setEditing(false);
    } catch (error) {
      message.error(String(error));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const startEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (saving) return;
    cancellingRef.current = false;
    editStartedAtRef.current = Date.now();
    setDraft(displayNote);
    setEditing(true);
  };

  const handleBlur = () => {
    if (Date.now() - editStartedAtRef.current < 150) {
      window.requestAnimationFrame(() => {
        inputRef.current?.focus({ cursor: "end" });
      });
      return;
    }
    void saveNote();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEdit();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void saveNote();
    }
  };

  if (variant === "compact") {
    const noteLabel = t("campaignDetail.creatorTableColumns.notes", {
      defaultValue: "Notes",
    });
    const editLabel = t("orderDetail.notes.addEditNote", {
      defaultValue: "Add/Edit note",
    });
    const buttonClassName =
      "flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

    return (
      <div
        className={`flex w-full min-w-0 items-center gap-2 rounded-md text-xs font-normal leading-[18px] tracking-[-0.12px] cursor-auto ${
          editing
            ? "border border-[#E5E5E5] bg-white px-[11px] py-[5px]"
            : "bg-[#F5F5F5] px-3 py-1.5"
        }`}
        style={{ maxWidth }}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        {editing ? (
          <>
            <div className="flex min-w-0 flex-1 items-center">
              <span key={noteLabel} className="shrink-0 whitespace-pre text-[#79716B]">
                {noteLabel}{": "}
              </span>
              <Input.TextArea
                ref={inputRef}
                aria-label={noteLabel}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={saving}
                variant="borderless"
                autoSize={{ minRows: 1, maxRows: 4 }}
                className="!min-h-[18px] !min-w-0 !flex-1 !resize-none !rounded-none !p-0 !text-xs !leading-[18px] !text-[#1C1917] !shadow-none"
              />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                aria-label={t("common.buttons.cancel")}
                title={t("common.buttons.cancel")}
                className={`${buttonClassName} text-[#79716B]`}
                onClick={cancelEdit}
                disabled={saving}
              >
                <CloseIcon className="size-4" />
              </button>
              <button
                type="button"
                aria-label={t("common.buttons.save")}
                title={t("common.buttons.save")}
                aria-busy={saving}
                className={`${buttonClassName} text-[#FF6011]`}
                onClick={() => void saveNote()}
                disabled={saving}
              >
                {saving ? <LoadingOutlined spin /> : <CheckIcon className="size-4" />}
              </button>
            </div>
          </>
        ) : (
          <>
            <Tooltip title={displayNote}>
              <span className="min-w-0 flex-1 truncate text-[#1C1917]">
                <span key={noteLabel} className="text-[#79716B]">
                  {noteLabel}{": "}
                </span>
                <span key={`note:${displayNote}`}>{displayNote}</span>
              </span>
            </Tooltip>
            <button
              type="button"
              aria-label={editLabel}
              title={editLabel}
              className={`${buttonClassName} text-[#79716B]`}
              onClick={startEdit}
            >
              <EditIcon className="size-4" />
            </button>
          </>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div
        className="relative min-w-[120px]"
        style={{ maxWidth }}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        <Input.TextArea
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={saving}
          autoSize={{ minRows: 1, maxRows: 4 }}
          className="text-sm !min-h-[32px]"
        />
        {saving && (
          <div className="absolute right-2 top-2">
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          </div>
        )}
      </div>
    );
  }

  const previewText = displayNote || "-";

  return (
    <Tooltip
      title={
        displayNote ||
        t("campaignDetail.creatorTableColumns.notesDoubleClickHint", {
          defaultValue: "Double-click to edit",
        })
      }
    >
      <div
        className="overflow-hidden text-ellipsis whitespace-nowrap line-clamp-2 cursor-text"
        style={{ maxWidth }}
        onDoubleClick={startEdit}
      >
        <span key={previewText}>{previewText}</span>
      </div>
    </Tooltip>
  );
};

export default InlineEditableNote;
